import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { PermissionsEnum } from '@/auth/interfaces';
import { gridSizeMdLg12, gridSizeMdLg6, ROUTER_PATHS, useCheckPermissions } from '@/shared';
import {
  CreateIpv4GroupParams,
  useUpdateIpv4Group,
  useCreateIpv4GroupAsync,
  CeleryTaskStatus,
} from '@/shared/actions/erp';
import {
  CustomAutocompleteArrString,
  CustomNumberTextField,
  CustomTextArea,
  CustomTextField,
  SingleFormBoxScene,
  SwitchComponenteForm,
} from '@/shared/components/ui';
import { Ipv4Group, TipoUsoIPv4 } from '@/shared/interfaces/erp/system-settings';
import { grupoIpv4Schema, texto } from '@/shared/utils';
import { Grid } from '@mui/material';
import { CustomAlertCard } from '@/shared/components/ui/Alerts';
import { TIPO_USO_GRUPO_IPV4 } from '../../constants';
import { Ipv4CreationProgressModal } from '../Ipv4CreationProgressModal';
import { useUiStore } from '@/store/ui';
import { AlertSeverityEnum } from '@/shared/interfaces';

export type SaveIpv4GroupsProps = {
  titlePage: string;
  editIpv4Group?: Ipv4Group;
};

export type SaveFormData = CreateIpv4GroupParams & {
  tipo_esquema?: string;
  routers_selected?: string[];
  bras_selected?: string;
}

const SaveIpv4Groups: React.FC<SaveIpv4GroupsProps> = ({
  titlePage,
  editIpv4Group,
}) => {
  useCheckPermissions(PermissionsEnum.api_view_router);
  const returnUrlIpV4 = ROUTER_PATHS.configuracionRED.grupoIpv4Nav;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  // Estado para el modal de progreso
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [createdGroupName, setCreatedGroupName] = useState<string>('');

  ///* fetch data
  // const {
  //   data: routersPagingRes,
  //   isLoading: isLoadRouters,
  //   isRefetching: isRefetchingRouters,
  // } = useFetchRouters({
  //   tiene_grupo_ipv_4: false,
  //   page_size: 1000,
  // });

  ///* mutations
  const createIpv4GroupMutation = useCreateIpv4GroupAsync({
    onTaskStarted: (taskId, groupData) => {
      setCurrentTaskId(taskId);
      setCreatedGroupName(groupData.nombre || 'Grupo IPv4');
      setShowProgressModal(true);
    },
    onError: (error) => {
      setAlert(true, error || 'Error al crear el grupo IPv4', AlertSeverityEnum.error);
    },
  });

  const updateIpv4GroupMutation = useUpdateIpv4Group({
    navigate,
    returnUrl: returnUrlIpV4,
  });


  // Callback para cuando el proceso de creación termine
  const handleProgressComplete = useCallback((success: boolean, result?: CeleryTaskStatus['result']) => {
    setShowProgressModal(false);
    setCurrentTaskId(null);

    if (success) {
      queryClient.invalidateQueries({ queryKey: ['ipv4Groups'] });
      setAlert(
        true,
        result?.message || 'Grupo IPv4 creado correctamente',
        AlertSeverityEnum.success
      );
      navigate(returnUrlIpV4);
    } else {
      setAlert(
        true,
        result?.message || 'Error al crear las IPs del grupo',
        AlertSeverityEnum.error
      );
    }
  }, [queryClient, setAlert, navigate, returnUrlIpV4]);


  ///* form
  const form = useForm<SaveFormData>({
    resolver: yupResolver(grupoIpv4Schema as any),
    defaultValues: {
      tipo_uso: TipoUsoIPv4.ESTATICO,
      estado: true,
    },
  });
  const {

    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  ///* handlers
  const onSave = () => {
    if (!isValid) return;
    const dataForm= form.getValues();
    if(!!!dataForm?.bras){
      delete dataForm?.bras
    }
    ///* edit
    if (editIpv4Group?.id_grupoipv4) {
      delete dataForm.router_data;
      return updateIpv4GroupMutation.mutate({
        id: editIpv4Group.id_grupoipv4,
        data: { ...dataForm, 
          gateway: dataForm?.gateway, 
          ipv_4: dataForm?.ipv_4,
          cidr: dataForm?.cidr as string
        },
      });
    }

    ///* create - usar método asíncrono con Celery
    setCreatedGroupName(dataForm.nombre || 'Grupo IPv4');
    return createIpv4GroupMutation.mutateAsync({
      ...dataForm,
      gateway: dataForm?.gateway,
      ipv_4: dataForm?.ipv_4,
      cidr: dataForm?.cidr as string,
    });
  };

  ///* effects ---------------------
  useEffect(() => {
    if (!editIpv4Group) return;

    reset(editIpv4Group);
  }, [editIpv4Group]);

  // if (!!editIpv4Group)
  //   return <LoaderComponent />;

  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlIpV4);
      }}
      onSave={handleSubmit(onSave)}
    >
      {
        +form.watch().cidr < 16 && (
          <Grid item xs={12}>
            <CustomAlertCard
              alertTitle='Información'
              alertMessage='Estás a punto de crear una barra inferior a la recomendada. Tomar en cuenta que el máximo de IPs creadas es de 65,536.'
            />
          </Grid>
        )
      }
      <CustomTextField
        label="Nombre"
        name="nombre"
        control={form.control}
        defaultValue={form.getValues().nombre || ''}
        error={errors.nombre}
        helperText={errors.nombre?.message}
        type='none'
      />
      <CustomTextArea
        label="Descripción"
        name="descripcion"
        control={form.control}
        defaultValue={form.getValues().descripcion || ''}
        error={errors.descripcion}
        helperText={errors.descripcion?.message}
        required={false}
      />
      <CustomTextField
        label="IPv4"
        name="ipv_4"
        control={form.control}
        defaultValue={form.getValues().ipv_4 || ''}
        error={errors.ipv_4}
        helperText={errors.ipv_4?.message}
        size={gridSizeMdLg6}
        type="ipv4"
      />
      <CustomNumberTextField
        label="CIDR"
        name="cidr"
        control={form.control}
        defaultValue={form.getValues().cidr || ''}
        error={errors.cidr}
        helperText={errors.cidr?.message}
        size={gridSizeMdLg6}
      />
      <CustomTextField
        label="Gateway"
        name="gateway"
        control={form.control}
        defaultValue={form.getValues().gateway || ''}
        error={errors.gateway}
        helperText={errors.gateway?.message}
        size={gridSizeMdLg12}
        type="ipv4"
        disabled={!(form.watch("ipv_4") && form.watch("cidr"))}
      />

      {/* 
      // =======================================================================
      // Asociación de Grupos IPV4 con Bras
      // =======================================================================
       */}
      {
        form.getValues()?.router_data 
        && (form.getValues()?.router_data?.length||0) > 0 ?
        <>
          <CustomAlertCard
          alertColor='info'
          alertTitle="Nota"
          alertMessage={texto.notificacion_grupoipv4_router} 
          bgColorAlert="SKY"/>
        </>
        :null
      }
      {/* <SampleCheckbox
        label='Habilitar Esquema Bras'
        name='es_bras'
        control={form.control}
        defaultValue={!!form.getValues().es_bras || editIpv4Group?.es_bras || false}
        size={gridSizeMdLg6}
        onChangeValue={v=>{
          reset({
            ...form.getValues(),
            es_bras: v,
            bras:!v?null:form.getValues()?.bras,
          })
        }}
      /> */}
      {/* <CustomAutocomplete<IBras>
        label="Seleccionar Bras"
        name="bras"
        options={dataBras?.data.data ?? []}
        valueKey="nombre"
        actualValueKey="codigo"
        defaultValue={form.getValues().bras}
        isLoadingData={isLoadingBras}
        control={form.control}
        error={form.formState.errors.bras}
        helperText={form.formState.errors.bras?.message}
        size={gridSizeMdLg6}
        required={false}
        disabled={!!!form.getValues()?.es_bras}
      /> */}
      {/* <CustomDynamicSearch<IBras>
        label="BRAS"
        name='bras'
        valueKey={"nombre"}
        actualValueKey={"codigo"}
        defaultValue={form.getValues().bras || editIpv4Group?.bras}
        functionToGetData={getBras}
        error={errors.bras}
        control={form.control}
        helperText={errors.bras?.message}
        disabled={!!!form.getValues()?.es_bras}
        size={gridSizeMdLg6}
      /> */}




      <CustomTextField
        label="Vlan"
        name="vlan"
        control={form.control}
        defaultValue={form.getValues().vlan || ''}
        error={errors.vlan}
        helperText={errors.vlan?.message}
        size={gridSizeMdLg6}
      />


      {/* <CustomNumberTextField
        isPositive={true}
        label="CIDR"
        name="cidr"
        max={32}
        min={1}
        defaultValue={form.getValues().cidr || 32}
        control={form.control}
        error={errors.cidr}
        helperText={errors.cidr?.message}
        size={gridSizeMdLg6}
      /> */}

      <CustomAutocompleteArrString
        label="Tipo de uso"
        name="tipo_uso"
        options={TIPO_USO_GRUPO_IPV4}
        control={form.control}
        disableClearable
        defaultValue={form.getValues().tipo_uso || ''}
        error={errors.tipo_uso}
        helperText={errors.tipo_uso?.message?.toString() ?? ''}
        isLoadingData={false}
        size={gridSizeMdLg6}
      />

      {/* <SampleCheckbox
        label='Habilitar Esquema Bras'
        name='es_bras'
        control={form.control}
        defaultValue={!!form.getValues().es_bras || editIpv4Group?.es_bras || false}
        size={gridSizeMdLg6}
        onChangeValue={v=>{
          reset({
            ...form.getValues(),
            es_bras: v,
            bras:!v?null:form.getValues()?.bras,
          })
        }}
      /> */}
      <SwitchComponenteForm
        label={`${form.watch()?.es_bras ? 'Habilitado' : 'Deshabilitado'} para BRAS`}
        size={gridSizeMdLg6}
        control={form.control}
        defaultValue={!!form.getValues()?.es_bras}
        name="es_bras"
      />


      <SwitchComponenteForm
        label={form.watch()?.estado ? 'Activo' : 'Inactivo'}
        size={gridSizeMdLg6}
        control={form.control}
        defaultValue={!!form.getValues()?.estado}
        name="estado"
      />
      {/* <SampleCheckbox
        label="Es BRAS"
        name="es_bras"
        control={form.control}
        defaultValue={editIpv4Group?.es_bras||!!form.getValues().es_bras}
        // isState
        size={gridSizeMdLg3}
      /> */}

{/*

      {!!editIpv4Group && (
        <>
          <Grid item xs={12}>
            <TabTextLabel text={'Router Asiganado'} mt={0} pb={2} pl={0} pt={0} />
          </Grid>

          {
            editIpv4Group?.router_data?.map((value, index) => {
              return <CustomTextFieldNoForm
                key={value.codigo}
                disabled={true}
                label={`Router Asignado ${index + 1}`}
                defaultValue={value.nombre}
                size={gridSizeMdLg6}
              />
            })

          }
        </>
      )
      }

      <Grid item xs={12}>
        <Typography variant='body2'>
          Nota: Una vez que se asigna un router, no se puede cambiar ni eliminar.
        </Typography>
      </Grid>

      <CustomAutocompleteMultiple<Router>
        label="Router"
        name="routers"
        textFieldKey="custom-autocomplete-router"
        valueKey="nombre"
        actualValueKey="codigo"
        // options
        options={routersPagingRes?.data.data || []}
        defaultValue={
          []
        }
        isLoadingData={isLoadRouters}
        // errors
        control={form.control}
        error={!!errors.routers}
        helperText={errors.routers?.message}
        disabled={false}
        size={gridSizeMdLg12}
        useSpecificKey
        specificKey="codigo"
      /> */}

      {/* Modal de progreso de creación de IPs */}
      <Ipv4CreationProgressModal
        open={showProgressModal}
        taskId={currentTaskId}
        onComplete={handleProgressComplete}
        groupName={createdGroupName}
      />

    </SingleFormBoxScene>
  );
};

export default SaveIpv4Groups;
